import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { BookOpen, Menu } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "./ThemeToggle";
import { MobileMenu } from "./MobileMenu";
import { MainNavigationMenu } from "./navigation/MainNavigationMenu";
import { UserMenu } from "./navigation/UserMenu";

const Navbar = () => {
  const [user, setUser] = useState<any>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [profile, setProfile] = useState<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  useEffect(() => {
    checkUser();

    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === "SIGNED_IN") {
          setUser(session?.user ?? null);
          await fetchProfile(session?.user?.id);
          if (location.pathname === "/") {
            navigate("/dashboard", { replace: true });
          }
        } else if (event === "SIGNED_OUT") {
          setUser(null);
          setProfile(null);
          navigate("/", { replace: true });
          toast({
            title: "Signed out successfully",
            description: "You have been signed out of your account.",
          });
        }
      }
    );

    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, [navigate, toast, location.pathname]);

  const fetchProfile = async (userId: string | undefined) => {
    if (!userId) return;

    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .single();

      if (error) throw error;
      setProfile(data);
    } catch (error) {
      console.error("Error fetching profile:", error);
    }
  };

  const checkUser = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      setUser(user);
      if (user) {
        await fetchProfile(user.id);
      }
    } catch (error) {
      console.error("Error checking user:", error);
    }
  };

  const handleSignOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast({
        title: "Error signing out",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  return (
    <nav className="fixed top-0 z-50 w-full border-b bg-background/95 dark:bg-gray-950/95 backdrop-blur-md supports-[backdrop-filter]:bg-background/80 dark:border-gray-800 shadow-sm">
      <div className="container flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center space-x-2">
          <BookOpen className="h-6 w-6 text-primary" />
          <span className="font-bold text-xl text-foreground dark:text-white">
            Numberz Insight
          </span>
        </Link>

        <div className="hidden md:flex md:flex-1 md:justify-center">
          <MainNavigationMenu />
        </div>

        <div className="flex items-center space-x-6">
          <ThemeToggle />
          <div className="hidden md:block">
            <UserMenu user={user} profile={profile} onSignOut={handleSignOut} />
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setIsMobileMenuOpen(true)}
          >
            <Menu className="h-5 w-5" />
            <span className="sr-only">Toggle menu</span>
          </Button>
        </div>
      </div>

      <MobileMenu
        user={user}
        profile={profile}
        onSignOut={handleSignOut}
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
      />
    </nav>
  );
};

export default Navbar;
